import { useObserva } from "@morganisbatman/observa-client-react";
import { useRouter } from "expo-router";
import { useMemo, useState } from "react";
import {
  KeyboardAvoidingView,
  Platform,
  Pressable,
  StyleSheet,
  Text,
  TextInput,
  View,
} from "react-native";

import { fontFamily, layout, radius, space, textStyle } from "@passfirst/ui";

import { authClient } from "../src/auth-client";
import { Button } from "../src/components/button";
import { SocialButtons } from "../src/social-buttons";
import { type Colors, useColors } from "../src/theme";

export default function SignUp(): JSX.Element {
  const router = useRouter();
  const observa = useObserva();
  const colors = useColors();
  const styles = useMemo(() => makeStyles(colors), [colors]);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // BetterAuth signup with `requireEmailVerification` does NOT
  // auto-sign-in — the user has to click the confirmation link in
  // their inbox first. Show a "check your email" state rather than
  // bouncing them to the home page where they'd appear anonymous.
  const [verificationSent, setVerificationSent] = useState(false);
  // Resend state for the "Check your email" screen: "idle" → "sending"
  // → "sent" (brief cooldown so the button can't be spammed).
  const [resendState, setResendState] = useState<"idle" | "sending" | "sent">(
    "idle",
  );

  async function handleResend(): Promise<void> {
    setResendState("sending");
    setError(null);
    void observa.event({
      eventType: "signup.verification_sent",
      payload: {},
    });
    try {
      // Unauthenticated BetterAuth endpoint (POST /auth/send-verification-email).
      // Re-issues the verification link without needing a session — the
      // user hasn't verified yet, so they can't be logged in.
      const res = await authClient.sendVerificationEmail({
        email,
        callbackURL: "/",
      });
      if (res.error) {
        setError(res.error.message ?? "couldn't resend verification email");
        setResendState("idle");
        return;
      }
      setResendState("sent");
      // Brief cooldown, then re-enable the button for another try.
      setTimeout(() => setResendState("idle"), 30_000);
    } catch (err) {
      setError(err instanceof Error ? err.message : "couldn't resend");
      setResendState("idle");
    }
  }

  function handleUseDifferentEmail(): void {
    // Return to the editable form. Keep the typed name/password so the
    // user only has to fix the address.
    setVerificationSent(false);
    setResendState("idle");
    setError(null);
  }

  async function handleSubmit(): Promise<void> {
    setBusy(true);
    setError(null);
    void observa.event({
      eventType: "signup.submitted",
      payload: { method: "email" },
    });
    try {
      const res = await authClient.signUp.email({
        email,
        password,
        name: name || email.split("@")[0] || "New User",
      });
      if (res.error) {
        const reason = res.error.message ?? "sign-up failed";
        setError(reason);
        void observa.event({
          eventType: "signup.failed",
          payload: { method: "email", reason },
        });
        return;
      }
      setVerificationSent(true);
      void observa.event({
        eventType: "signup.verification_sent",
        payload: {},
      });
    } catch (err) {
      const reason = err instanceof Error ? err.message : "sign-up failed";
      setError(reason);
      void observa.event({
        eventType: "signup.failed",
        payload: { method: "email", reason },
      });
    } finally {
      setBusy(false);
    }
  }

  if (verificationSent) {
    return (
      <View style={styles.container}>
        <Text style={styles.title}>Check your email</Text>
        <View style={styles.form}>
          <Text
            style={styles.notice}
            accessibilityLiveRegion="polite"
          >
            We&apos;ve sent a confirmation link to {email}. Click it to finish
            signing up, then come back here to log in.
          </Text>
          {error ? (
            <Text style={styles.error} accessibilityLiveRegion="polite">
              {error}
            </Text>
          ) : null}
          {resendState === "sent" ? (
            <Text style={styles.sentNotice} accessibilityLiveRegion="polite">
              Sent. Check your inbox again.
            </Text>
          ) : null}
          <Button
            label="Back to log in"
            onPress={() => router.replace("/login")}
            fullWidth
          />
          <Button
            label={
              resendState === "sending"
                ? "Sending…"
                : resendState === "sent"
                  ? "Link sent"
                  : "Didn't get it? Resend link"
            }
            onPress={handleResend}
            disabled={resendState !== "idle"}
            variant="ghost"
            fullWidth
            accessibilityLabel="Resend confirmation link"
          />
          <Button
            label="Use a different email"
            onPress={handleUseDifferentEmail}
            variant="ghost"
            fullWidth
            accessibilityLabel="Use a different email address"
          />
        </View>
      </View>
    );
  }

  return (
    <KeyboardAvoidingView
      behavior={Platform.OS === "ios" ? "padding" : undefined}
      style={{ flex: 1 }}
    >
    <View style={styles.container}>
      <Text style={styles.title}>Create account</Text>
      <View style={styles.form}>
        <TextInput
          style={styles.input}
          placeholder="Name (optional)"
          placeholderTextColor={colors.textMuted}
          value={name}
          onChangeText={setName}
          autoCapitalize="words"
          accessibilityLabel="Name (optional)"
        />
        <TextInput
          style={styles.input}
          placeholder="Email"
          placeholderTextColor={colors.textMuted}
          value={email}
          onChangeText={setEmail}
          autoCapitalize="none"
          keyboardType="email-address"
          autoComplete="email"
          accessibilityLabel="Email address"
        />
        <TextInput
          style={styles.input}
          placeholder="Password (8+ chars)"
          placeholderTextColor={colors.textMuted}
          value={password}
          onChangeText={setPassword}
          secureTextEntry
          autoComplete="new-password"
          accessibilityLabel="Password, eight characters or more"
        />
        {error ? (
          <Text style={styles.error} accessibilityLiveRegion="polite">
            {error}
          </Text>
        ) : null}
        <Button
          label="Sign up"
          onPress={handleSubmit}
          disabled={!email || password.length < 8}
          busy={busy}
          fullWidth
          accessibilityLabel="Sign up"
        />
        <SocialButtons callbackURL="/" intent="signup" />

        <Pressable
          onPress={() => router.push("/login")}
          accessibilityRole="link"
          accessibilityLabel="Log in to existing account"
          style={styles.linkBtn}
        >
          <Text style={styles.linkText}>Have an account? Log in</Text>
        </Pressable>
      </View>
    </View>
    </KeyboardAvoidingView>
  );
}

function makeStyles(colors: Colors) {
  return StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background,
    alignItems: "center",
    justifyContent: "center",
    padding: space[6],
  },
  title: { ...textStyle.h2, color: colors.heading },
  form: { marginTop: space[6], gap: space[3], width: layout.formMaxWidth },
  input: {
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: radius.md,
    paddingVertical: space[3],
    paddingHorizontal: space[4],
    fontSize: 16,
    fontFamily: fontFamily.regular,
    color: colors.textPrimary,
    backgroundColor: colors.surface,
  },
  error: { ...textStyle.bodySmall, color: colors.error },
  notice: { ...textStyle.body, color: colors.textPrimary, textAlign: "center" },
  sentNotice: {
    ...textStyle.bodySmall,
    color: colors.success,
    textAlign: "center",
  },
  linkBtn: { paddingVertical: space[2], alignItems: "center" },
  linkText: { ...textStyle.bodySmall, color: colors.textMuted },
  });
}
